import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X as XIcon, Send, Paperclip, RotateCcw } from 'lucide-react';
import { apiService } from '../../../services/apiService';
import type { InvoiceDocumentDetail, InvoiceEmailPreview, InvoiceLanguage } from '../../../services/apiService';
import { useToast } from '../../../contexts/ToastContext';
import { LANG_LABEL_KEY } from './invoiceHelpers';

interface SendInvoiceDialogProps {
    doc: InvoiceDocumentDetail;
    onClose: () => void;
    onSent?: () => void;
}

const inputClass =
    'w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';
const labelClass = 'block text-[11px] font-medium uppercase tracking-wider text-brand-text-muted mb-1';

const SendInvoiceDialog: React.FC<SendInvoiceDialogProps> = ({ doc, onClose, onSent }) => {
    const { t } = useTranslation();
    const { addToast } = useToast();
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [to, setTo] = useState('');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [language, setLanguage] = useState<InvoiceLanguage>(doc.language);

    const loadPreview = async (lang: InvoiceLanguage) => {
        try {
            setLoading(true);
            const res = await apiService.previewInvoiceEmail(doc.id, lang);
            const p: InvoiceEmailPreview = res.data.data;
            setTo(p.to);
            setSubject(p.subject);
            setBody(p.body);
            setLanguage(p.language);
        } catch (e: any) {
            addToast(e?.response?.data?.message || t('billing.send.previewError'), 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPreview(doc.language);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doc.id]);

    const resetToTemplate = () => loadPreview(language);

    const send = async () => {
        if (!to.trim() || !subject.trim()) {
            addToast(t('billing.send.recipientSubjectRequired'), 'error');
            return;
        }
        try {
            setSending(true);
            await apiService.sendInvoiceEmail(doc.id, { to: to.trim(), subject, body });
            addToast(t('billing.send.sent'), 'success');
            onSent?.();
            onClose();
        } catch (e: any) {
            addToast(e?.response?.data?.message || t('billing.send.sendFailed'), 'error');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-12 px-4 bg-black/50 overflow-y-auto" onClick={onClose}>
            <div className="w-full max-w-2xl rounded-xl bg-brand-surface border border-brand-border shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-brand-border">
                    <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-brand-text">{t('billing.send.title')}</h3>
                        <p className="text-xs text-brand-text-muted">
                            {doc.number} · {doc.billing_address?.name}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-1 text-brand-text-muted hover:text-brand-text rounded cursor-pointer" aria-label={t('common.close')}>
                        <XIcon className="h-4 w-4" strokeWidth={1.75} />
                    </button>
                </div>

                {loading ? (
                    <div className="p-10 text-center text-sm text-brand-text-muted">{t('billing.send.loadingTemplate')}</div>
                ) : (
                    <>
                        <div className="p-5 space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div className="sm:col-span-2">
                                    <label className={labelClass}>{t('billing.send.toLabel')}</label>
                                    <input type="email" value={to} onChange={(e) => setTo(e.target.value)} className={inputClass} placeholder={t('billing.send.toPlaceholder')} />
                                </div>
                                <div>
                                    <label className={labelClass}>{t('billing.send.languageLabel')}</label>
                                    <select
                                        value={language}
                                        onChange={(e) => loadPreview(e.target.value as InvoiceLanguage)}
                                        className={inputClass}
                                    >
                                        {(Object.keys(LANG_LABEL_KEY) as InvoiceLanguage[]).map(l => (
                                            <option key={l} value={l}>{t(LANG_LABEL_KEY[l])}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className={labelClass}>{t('billing.send.subjectLabel')}</label>
                                <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputClass} />
                            </div>
                            <div>
                                <label className={labelClass}>{t('billing.send.bodyLabel')}</label>
                                <textarea rows={10} value={body} onChange={(e) => setBody(e.target.value)} className={inputClass + ' font-mono text-[12.5px] leading-relaxed'} />
                            </div>
                            <div className="flex items-center gap-2 px-3 py-2 bg-brand-surface-2/60 border border-brand-border rounded-md text-xs text-brand-text-muted">
                                <Paperclip className="h-3.5 w-3.5 text-brand-primary shrink-0" strokeWidth={1.75} />
                                <span>{t('billing.send.attachment')} <span className="font-medium text-brand-text">{doc.number || `doc-${doc.id}`}.pdf</span> {t('billing.send.attachmentNote')}</span>
                            </div>
                        </div>

                        <div className="px-5 py-3 border-t border-brand-border flex items-center justify-between">
                            <button
                                type="button"
                                onClick={resetToTemplate}
                                disabled={sending}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-text-muted hover:text-brand-text hover:bg-brand-surface-2 rounded transition-colors cursor-pointer disabled:opacity-50"
                            >
                                <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.75} />
                                {t('billing.send.reloadTemplate')}
                            </button>
                            <div className="flex items-center gap-2">
                                <button onClick={onClose} disabled={sending} className="px-3 py-1.5 text-xs font-medium text-brand-text-muted hover:bg-brand-surface-2 rounded cursor-pointer disabled:opacity-50">
                                    {t('common.cancel')}
                                </button>
                                <button
                                    onClick={send}
                                    disabled={sending || !to || !subject}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Send className="h-3.5 w-3.5" strokeWidth={2} />
                                    {sending ? t('billing.send.sending') : t('billing.send.send')}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SendInvoiceDialog;
